import React, { useEffect, useRef } from 'react';
import { Search, X, ChevronUp, ChevronDown } from 'lucide-react';
import { usePeek } from '../context/PeekContext';

/**
 * PeekSearch — floating top-center Find bar. Triggered by `/` or Cmd/Ctrl+F.
 * Highlights every match inside the current section only (scoped like Excel Find).
 * Enter jumps to the next match, Shift+Enter to the previous.
 */
export default function PeekSearch() {
  const { open, query, count, index, setOpen, setQuery, next, prev, reset } = usePeek();
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && inputRef.current) {
      const id = setTimeout(() => inputRef.current && inputRef.current.focus(), 20);
      return () => clearTimeout(id);
    }
  }, [open]);

  if (!open) return null;

  const close = () => {
    setOpen(false);
    reset();
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (e.shiftKey) prev();
      else next();
    }
  };

  const getStatus = () => {
    if (!query.trim()) return 'Type to find in this page';
    if (count > 0) return `${index} of ${count}`;
    return 'No matches';
  };
  const status = getStatus();

  return (
    <div
      className="fixed inset-0 z-[70] pointer-events-none"
      role="dialog"
      aria-label="Find in page"
      data-testid="peek-overlay"
      data-peek-exclude
    >
      {/* Click outside closes */}
      <button
        aria-label="Close find"
        onClick={close}
        className="absolute inset-0 pointer-events-auto"
        style={{ background: 'transparent', cursor: 'default' }}
      />

      <div
        className="pointer-events-auto absolute left-1/2 top-4 sm:top-6 -translate-x-1/2 w-[94%] max-w-[560px]"
        style={{
          background: 'rgba(10,7,4,0.92)',
          border: '1px solid rgba(245,165,36,0.55)',
          borderRadius: 10,
          boxShadow: '0 0 0 1px rgba(245,165,36,0.15), 0 30px 70px -20px rgba(245,165,36,0.45), 0 0 60px rgba(245,165,36,0.15)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)'
        }}
        data-peek-exclude
      >
        <div className="flex items-center gap-3 px-3 py-2.5">
          <Search size={15} strokeWidth={1.75} style={{ color: 'var(--amber)' }} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Find in this page…"
            data-testid="peek-input"
            className="flex-1 bg-transparent outline-none text-[14px] placeholder:opacity-50"
            style={{ color: 'var(--amber-2)', caretColor: 'var(--amber)' }}
          />

          {/* Match counter */}
          <span
            className="text-[11px] whitespace-nowrap tabular-nums select-none"
            style={{ color: count > 0 ? 'var(--amber)' : 'rgba(245,165,36,0.55)', minWidth: 78, textAlign: 'right' }}
            data-testid="peek-count"
          >
            {status}
          </span>

          {/* Prev / Next */}
          <button
            onClick={prev}
            disabled={count === 0}
            aria-label="Previous match"
            data-testid="peek-prev"
            className="press w-6 h-6 inline-flex items-center justify-center disabled:opacity-30"
            style={{ color: 'var(--amber)', border: '1px solid rgba(245,165,36,0.35)', borderRadius: 4 }}
          >
            <ChevronUp size={13} strokeWidth={2} />
          </button>
          <button
            onClick={next}
            disabled={count === 0}
            aria-label="Next match"
            data-testid="peek-next"
            className="press w-6 h-6 inline-flex items-center justify-center disabled:opacity-30"
            style={{ color: 'var(--amber)', border: '1px solid rgba(245,165,36,0.35)', borderRadius: 4 }}
          >
            <ChevronDown size={13} strokeWidth={2} />
          </button>

          <span className="hidden sm:inline text-[10px] px-1.5 py-0.5 select-none" style={{ border: '1px solid rgba(245,165,36,0.4)', color: 'var(--amber)', borderRadius: 3, letterSpacing: '0.08em' }}>ESC</span>

          <button
            onClick={close}
            aria-label="Close"
            data-testid="peek-close"
            className="press w-6 h-6 inline-flex items-center justify-center"
            style={{ color: 'var(--amber)' }}
          >
            <X size={14} strokeWidth={1.75} />
          </button>
        </div>

        <div className="px-3 pb-2 text-[11px] flex items-center gap-2" style={{ color: 'var(--muted)' }}>
          <span className="inline-block w-1.5 h-1.5 rounded-full pulse-dot" style={{ background: 'var(--amber)' }} />
          <span>Find is scoped to the current section. Enter · Shift+Enter to cycle.</span>
        </div>
      </div>
    </div>
  );
}
